import { useState, useEffect, Fragment } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Search, Trash2, Calendar, DollarSign, User, ChevronDown, ChevronUp, ExternalLink, Mail, Phone, FileText } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = ['Draft', 'Sent', 'Viewed', 'Accepted', 'Expired'];
const STATUS_STYLES = {
  'Draft': 'bg-[#F3F4F6] text-[#6B7280]',
  'Sent': 'bg-[#DBEAFE] text-[#2563EB]',
  'Viewed': 'bg-[#E0E7FF] text-[#4F46E5]',
  'Accepted': 'bg-[#D1FAE5] text-[#059669]',
  'Expired': 'bg-[#FEE2E2] text-[#DC2626]',
};

const QUOTE_TYPE_LABELS = {
  'critical-injury': 'Critical Injury',
  'performance-bonus': 'Performance Bonus',
  'transfer': 'Athlete Transfer',
};
const QUOTE_TYPE_STYLES = {
  'critical-injury': 'bg-[#CCFBF1] text-[#0D9488]',
  'performance-bonus': 'bg-[#FEF3C7] text-[#D97706]',
  'transfer': 'bg-[#EEF0FF] text-[#6B5CE7]',
};

const formatCurrency = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(v || 0);

const QuotePipelinePage = () => {
  const { getAuthHeaders } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const fetchQuotes = async () => {
    try {
      const res = await axios.get(`${API}/quotes/pipeline`, { headers: getAuthHeaders() });
      setQuotes(res.data);
    } catch {
      toast.error('Failed to load quote pipeline');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchQuotes(); }, [getAuthHeaders]);

  const updateStatus = async (quoteId, newStatus) => {
    try {
      await axios.put(`${API}/quotes/pipeline/${quoteId}/status`, { status: newStatus }, { headers: getAuthHeaders() });
      setQuotes(prev => prev.map(q => q.id === quoteId ? { ...q, status: newStatus } : q));
      toast.success(`Status updated to ${newStatus}`);
    } catch {
      toast.error('Failed to update status');
    }
  };

  const deleteQuote = async (quoteId) => {
    try {
      await axios.delete(`${API}/quotes/pipeline/${quoteId}`, { headers: getAuthHeaders() });
      setQuotes(prev => prev.filter(q => q.id !== quoteId));
      toast.success('Quote deleted');
    } catch {
      toast.error('Failed to delete quote');
    }
  };

  const copyShareLink = () => {
    const url = `${window.location.origin}/insurance`;
    try {
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      toast.success('Quote intake link copied');
    } catch {
      toast.info(`Share: ${url}`);
    }
  };

  const filtered = quotes.filter(q => {
    const term = searchTerm.toLowerCase();
    return (
      q.athlete_name?.toLowerCase().includes(term) ||
      q.school?.toLowerCase().includes(term) ||
      q.requester_name?.toLowerCase().includes(term) ||
      q.requester_email?.toLowerCase().includes(term) ||
      q.request_id?.toLowerCase().includes(term) ||
      q.sport?.toLowerCase().includes(term)
    );
  });

  const stats = {
    total: quotes.length,
    totalPremium: quotes.reduce((s, q) => s + (q.premium_amount || 0), 0),
    accepted: quotes.filter(q => q.status === 'Accepted').length,
    sent: quotes.filter(q => q.status === 'Sent').length,
  };

  return (
    <div className="p-6 space-y-6 bg-[#F8FAFC] min-h-screen">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A]">Quote Pipeline</h1>
          <p className="text-sm text-[#64748B]">Quote intake submissions from prospects</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" className="border-[#E2E8F0] text-[#475569]" onClick={copyShareLink} data-testid="copy-quote-link">
            <ExternalLink className="w-4 h-4 mr-2" /> Copy Intake Link
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Quotes', value: stats.total, icon: FileText },
          { label: 'Total Premium', value: formatCurrency(stats.totalPremium), icon: DollarSign },
          { label: 'Accepted', value: stats.accepted, icon: User },
          { label: 'Pending', value: stats.sent, icon: Calendar },
        ].map(s => (
          <Card key={s.label} className="bg-white border-[#E2E8F0] shadow-sm">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#EEF0FF] flex items-center justify-center">
                <s.icon className="w-5 h-5 text-[#6B5CE7]" />
              </div>
              <div>
                <p className="text-xs text-[#94A3B8]">{s.label}</p>
                <p className="text-lg font-bold text-[#0F172A]">{s.value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search */}
      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
            <Input placeholder="Search by athlete, school, requester, or request ID..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              className="pl-10 bg-[#F8FAFC] border-[#E2E8F0]" data-testid="pipeline-search" />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      {loading ? (
        <div className="text-center py-12 text-[#94A3B8]">Loading pipeline...</div>
      ) : filtered.length === 0 ? (
        <Card className="bg-white border-[#E2E8F0]">
          <CardContent className="p-12 text-center">
            <FileText className="w-12 h-12 text-[#94A3B8] mx-auto mb-4" />
            <h3 className="text-[#0F172A] font-semibold text-lg mb-2">{quotes.length === 0 ? 'No quotes yet' : 'No matching quotes'}</h3>
            <p className="text-[#64748B] mb-4">{quotes.length === 0 ? 'Share the intake link to start receiving quotes.' : 'Try adjusting your search.'}</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-white border-[#E2E8F0] shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="pipeline-table">
              <thead>
                <tr className="border-b border-[#E2E8F0] bg-[#F8FAFC]">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[#94A3B8] uppercase tracking-wider">Request ID</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[#94A3B8] uppercase tracking-wider">Type</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[#94A3B8] uppercase tracking-wider">Athlete</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[#94A3B8] uppercase tracking-wider">School</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[#94A3B8] uppercase tracking-wider">Sport</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[#94A3B8] uppercase tracking-wider">Requester</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-[#94A3B8] uppercase tracking-wider">Premium</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-[#94A3B8] uppercase tracking-wider">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[#94A3B8] uppercase tracking-wider">Date</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-[#94A3B8] uppercase tracking-wider"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(q => (
                  <Fragment key={q.id}>
                    <tr className="border-b border-[#E2E8F0] hover:bg-[#F8FAFC] transition-colors cursor-pointer"
                      onClick={() => setExpandedId(expandedId === q.id ? null : q.id)} data-testid={`quote-row-${q.id}`}>
                      <td className="px-4 py-3">
                        <code className="text-xs font-mono text-[#6B5CE7] bg-[#EEF0FF] px-2 py-0.5 rounded">{q.request_id}</code>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={`${QUOTE_TYPE_STYLES[q.quote_type] || 'bg-[#F3F4F6] text-[#6B7280]'} border-0 text-xs font-medium`}>
                          {QUOTE_TYPE_LABELS[q.quote_type] || 'Critical Injury'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-[#0F172A]">{q.athlete_name || '—'}</td>
                      <td className="px-4 py-3 text-sm text-[#475569]">{q.school}</td>
                      <td className="px-4 py-3 text-sm text-[#475569]">{q.sport}</td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-[#0F172A]">{q.requester_name}</p>
                        <p className="text-xs text-[#94A3B8]">{q.requester_email}</p>
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-semibold text-[#0F172A]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                        {q.premium_amount > 0 ? formatCurrency(q.premium_amount) : '—'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Badge className={`${STATUS_STYLES[q.status] || STATUS_STYLES['Draft']} border-0 text-xs font-medium`}>{q.status}</Badge>
                      </td>
                      <td className="px-4 py-3 text-sm text-[#94A3B8]">
                        {new Date(q.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {expandedId === q.id ? <ChevronUp className="w-4 h-4 text-[#94A3B8]" /> : <ChevronDown className="w-4 h-4 text-[#94A3B8]" />}
                        </div>
                      </td>
                    </tr>
                    {expandedId === q.id && (
                      <tr className="bg-[#F8FAFC]">
                        <td colSpan={10} className="px-6 py-5">
                          <div className="grid md:grid-cols-3 gap-6">
                            <div>
                              <p className="text-xs text-[#94A3B8] font-semibold uppercase mb-2">Requester Contact</p>
                              <div className="space-y-1.5 text-sm">
                                <p className="flex items-center gap-2 text-[#0F172A]"><User className="w-3.5 h-3.5 text-[#94A3B8]" /> {q.requester_name}</p>
                                <a href={`mailto:${q.requester_email}`} className="flex items-center gap-2 text-[#6B5CE7] hover:underline">
                                  <Mail className="w-3.5 h-3.5" /> {q.requester_email}
                                </a>
                                {q.requester_phone && <p className="flex items-center gap-2 text-[#475569]"><Phone className="w-3.5 h-3.5 text-[#94A3B8]" /> {q.requester_phone}</p>}
                                {q.requester_role && <p className="text-xs text-[#94A3B8]">Role: {q.requester_role}</p>}
                              </div>
                            </div>
                            <div>
                              <p className="text-xs text-[#94A3B8] font-semibold uppercase mb-2">
                                {q.quote_type === 'performance-bonus' ? 'Program Details' : q.quote_type === 'transfer' ? 'Transfer Details' : 'Athlete Details'}
                              </p>
                              {q.quote_type === 'performance-bonus' ? (
                                <div className="space-y-2 text-sm text-[#475569]">
                                  <p><strong>{q.school}</strong> · {q.sport}</p>
                                  {q.bonus_triggers?.length > 0 && (
                                    <div className="border border-[#E2E8F0] rounded-lg overflow-hidden">
                                      <table className="w-full text-xs">
                                        <tbody>
                                          {q.bonus_triggers.map((t, i) => {
                                            const label = typeof t === 'string' ? t : t.label;
                                            const amount = typeof t === 'string' ? null : t.amount;
                                            return (
                                              <tr key={i} className="border-b border-[#E2E8F0] last:border-0">
                                                <td className="px-3 py-1.5 text-[#475569]">{label}</td>
                                                <td className="px-3 py-1.5 text-right font-medium text-[#0F172A]">{amount != null ? formatCurrency(amount) : '—'}</td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                        <tfoot>
                                          <tr className="bg-[#FEF3C7]">
                                            <td className="px-3 py-1.5 font-semibold text-[#92400E]">Total Bonus</td>
                                            <td className="px-3 py-1.5 text-right font-bold text-[#92400E]">{formatCurrency(q.bonus_amount)}</td>
                                          </tr>
                                        </tfoot>
                                      </table>
                                    </div>
                                  )}
                                </div>
                              ) : q.quote_type === 'transfer' ? (
                                <div className="space-y-1.5 text-sm text-[#475569]">
                                  <p><strong>{q.athlete_name}</strong> — {q.school}</p>
                                  <p>{q.sport}{q.position ? ` · ${q.position}` : ''}</p>
                                  <p>Contract Value: <strong>{formatCurrency(q.coverage_amount)}</strong></p>
                                </div>
                              ) : (
                                <div className="space-y-1.5 text-sm text-[#475569]">
                                  <p><strong>{q.athlete_name}</strong> — {q.school}</p>
                                  <p>{q.sport} · {q.position}</p>
                                  <p>NIL Value: {formatCurrency(q.coverage_amount)}</p>
                                  {q.premium_amount > 0 && <p>Premium: <strong>{formatCurrency(q.premium_amount)}</strong></p>}
                                </div>
                              )}
                            </div>
                            <div>
                              <p className="text-xs text-[#94A3B8] font-semibold uppercase mb-2">Actions</p>
                              <div className="space-y-2">
                                <select value={q.status} onChange={e => updateStatus(q.id, e.target.value)}
                                  className="w-full px-3 py-2 bg-white border border-[#E2E8F0] rounded-lg text-sm text-[#0F172A] focus:outline-none focus:border-[#6B5CE7]"
                                  data-testid={`status-select-${q.id}`}>
                                  {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                                <Button variant="outline" size="sm" className="w-full text-[#DC2626] border-[#FCA5A5] hover:bg-[#FEF2F2]"
                                  onClick={(e) => { e.stopPropagation(); deleteQuote(q.id); }} data-testid={`delete-quote-${q.id}`}>
                                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                                </Button>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};

export default QuotePipelinePage;
